import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, Search, MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_LABELS = {
  client_intake: 'Intake', design_proposal: 'Propuesta Diseño',
  design_phase_a: 'Diseño A', design_phase_b: 'Diseño B', design_phase_c: 'Diseño C',
  installation_proposal: 'Propuesta Montaje', procurement: 'Procurement',
  installation: 'Instalación', completed: 'Entregado',
};

const STAGE_COLORS = {
  client_intake: '#C9B8A8', design_proposal: '#C9A66B', design_phase_a: '#D4A574',
  design_phase_b: '#D4A574', design_phase_c: '#D4A574',
  installation_proposal: '#C9A66B', procurement: '#8B9F8D',
  installation: '#8B9F8D', completed: '#6B8F6B',
};

export default function OSProjects() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(searchParams.get('new') === '1');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', client_name: '', client_email: '', client_phone: '', address: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await authFetch('/api/projects/');
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch('/api/projects/', { method: 'POST', body: JSON.stringify(form) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      const p = await res.json();
      toast.success('Proyecto creado');
      setShowModal(false);
      setForm({ name: '', client_name: '', client_email: '', client_phone: '', address: '', description: '' });
      load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.client_name.toLowerCase().includes(search.toLowerCase())
  );

  const canCreate = user?.role === 'admin' || user?.role === 'designer';

  return (
    <div data-testid="os-projects-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Proyectos</h1>
          <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>{projects.length} proyecto{projects.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#C9B8A8' }} />
            <input
              data-testid="project-search"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar proyecto..."
              className="pl-9 pr-4 py-2 rounded-lg text-sm w-full sm:w-60 outline-none"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
            />
          </div>
          {canCreate && (
            <button
              data-testid="create-project-btn"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-light tracking-wide transition-all duration-300 whitespace-nowrap"
              style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#C9A66B'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2D2D2D'}
            >
              <Plus size={16} /> Nuevo
            </button>
          )}
        </div>
      </div>

      {/* Project Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border p-16 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
          <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>{search ? 'Sin resultados' : 'No hay proyectos aún'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => navigate(`/os/projects/${p.id}`)}
              className="rounded-xl border p-5 cursor-pointer transition-all duration-200 hover:shadow-md group"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-base font-medium truncate pr-2" style={{ color: '#2D2D2D' }}>{p.name}</h3>
                <span className="text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap font-medium shrink-0"
                  style={{ backgroundColor: (STAGE_COLORS[p.stage] || '#C9B8A8') + '20', color: STAGE_COLORS[p.stage] || '#C9B8A8' }}>
                  {STAGE_LABELS[p.stage] || p.stage}
                </span>
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-light flex items-center gap-2" style={{ color: '#2D2D2D' }}>
                  {p.client_name}
                </p>
                {p.address && (
                  <p className="text-xs flex items-center gap-1.5" style={{ color: '#C9B8A8' }}>
                    <MapPin size={12} /> {p.address}
                  </p>
                )}
                {p.client_phone && (
                  <p className="text-xs flex items-center gap-1.5" style={{ color: '#C9B8A8' }}>
                    <Phone size={12} /> {p.client_phone}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="rounded-2xl border p-8 w-full max-w-lg shadow-xl" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Nuevo Proyecto</h2>
              <button onClick={() => setShowModal(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {[
                { key: 'name', label: 'Nombre del Proyecto', required: true, placeholder: 'Ej: Residencia García' },
                { key: 'client_name', label: 'Nombre del Cliente', required: true, placeholder: 'Nombre completo' },
                { key: 'client_email', label: 'Email del Cliente', type: 'email', placeholder: 'cliente@email.com' },
                { key: 'client_phone', label: 'Teléfono', placeholder: '+52 ...' },
                { key: 'address', label: 'Dirección', placeholder: 'Dirección del proyecto' },
              ].map(({ key, label, required, type, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>{label}</label>
                  <input
                    data-testid={`project-field-${key}`}
                    type={type || 'text'}
                    value={form[key]}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    required={required}
                    placeholder={placeholder}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Descripción</label>
                <textarea
                  data-testid="project-field-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Breve descripción del proyecto..."
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
                  style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg text-sm border transition-colors" style={{ borderColor: '#E8E3D8', color: '#2D2D2D' }}>
                  Cancelar
                </button>
                <button
                  data-testid="project-submit-btn"
                  type="submit" disabled={saving}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all duration-300 disabled:opacity-50"
                  style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
                >
                  {saving ? 'Creando...' : 'Crear Proyecto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
